// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {dataTableFormatTime} from 'Utils/time-utils';
import {getFormattedCurrency} from 'Utils/unit-conversion-utils';
import DataGridWrapper from 'Ui/components/common/data-table/data-grid-wrapper';
import {isAggregationFooterRow} from 'Ui/components/common/data-table/data-grid-utils';

function makeCurrencyColumn(headerName, field, footerValue, currencyCell) {
    return {
        headerName,
        field,
        minWidth: 150,
        flex: 1,
        type: 'number',
        valueFormatter: (value) => currencyCell({
            value
        }),
        renderCell: (params) => {
            if (isAggregationFooterRow(params)) {
                return currencyCell({
                    value: footerValue
                });
            }

            return currencyCell({
                value: params.value
            });
        }
    };
}

function getDataGridColumns(translations, costReportData, currencyCell) {
    const formattedDate = dataTableFormatTime(costReportData.dateSelect, costReportData.dateScale);
    const footerDateLabel = `${formattedDate} ${translations.ONLINK_TOTAL}`;

    return [
        {
            headerName: translations.DATE,
            field: 'dateSelect',
            minWidth: 200,
            flex: 1,
            valueGetter: (value, row) => value ? dataTableFormatTime(value, row.dateScale) : '',
            renderCell: (params) => {
                if (isAggregationFooterRow(params)) {
                    return footerDateLabel;
                }

                return params.value;
            }
        },
        makeCurrencyColumn(translations.SALES_SYSTEM_TRANSACTION_PURCHASE, 'purchase', costReportData.purchase, currencyCell),
        makeCurrencyColumn(translations.ONLINK_PROPERTY_TAX, 'propertyTax', costReportData.propertyTax, currencyCell),
        makeCurrencyColumn(translations.ONLINK_SALES_TAX, 'salesTax', costReportData.salesTax, currencyCell),
        makeCurrencyColumn(translations.PARTS, 'partsCost', costReportData.partsCost, currencyCell),
        makeCurrencyColumn(translations.ONLINK_SERVICE, 'serviceCost', costReportData.serviceCost, currencyCell),
        makeCurrencyColumn(translations.ONLINK_TOTAL, 'totalCost', costReportData.totalCost, currencyCell)
    ];
}

function CostReportDataTable(props) {
    const {
        costReportData,
        membership,
        translations
    } = props;

    const currencyCell = React.useCallback(({value}) => getFormattedCurrency(value, {
        currencyPreference: membership.currencyPreference
    }), [membership.currencyPreference]);

    return (
        <div className={'ops-center-table-container'}>
            <DataGridWrapper
                aggregation={{
                    dateSelect: {
                        type: 'size',
                        config: {}
                    },
                    purchase: {
                        type: 'sum',
                        config: {}
                    },
                    propertyTax: {
                        type: 'sum',
                        config: {}
                    },
                    salesTax: {
                        type: 'sum',
                        config: {}
                    },
                    partsCost: {
                        type: 'sum',
                        config: {}
                    },
                    serviceCost: {
                        type: 'sum',
                        config: {}
                    },
                    totalCost: {
                        type: 'sum',
                        config: {}
                    }
                }}
                columns={getDataGridColumns(translations, costReportData, currencyCell)}
                getRowId={(row) => row.dateSelect}
                initialState={{
                    sorting: {
                        sortModel: [{
                            field: 'dateSelect',
                            sort: 'desc'
                        }]
                    }
                }}
                pinnedColumns={{
                    left: [],
                    right: []
                }}
                rows={costReportData.dateChildren}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

CostReportDataTable.propTypes = {
    costReportData: PropTypes.object,
    membership: PropTypes.membership,
    translations: PropTypes.translations
};

export default CostReportDataTable;
